import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { apiListTemplates, apiDeleteTemplate, apiDuplicateTemplate, FormTemplate, TemplateSection } from '../lib/api'
import { EmptyState } from './EmptyState'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import { Skeleton } from './ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog'
import { TemplateEditor } from './TemplateEditor'

function TemplateSkeleton() {
  return (
    <div className="rounded-lg border bg-white/80 p-5 space-y-3">
      <div className="flex items-start justify-between">
        <div className="space-y-2 flex-1">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-4 w-24" />
        </div>
        <Skeleton className="h-6 w-20 rounded-full" />
      </div>
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-3/4" />
      <div className="flex gap-2 pt-1">
        <Skeleton className="h-8 w-20" />
        <Skeleton className="h-8 w-20" />
      </div>
    </div>
  )
}

function TemplatePreviewDialog({
  template,
  open,
  onClose,
}: {
  template: FormTemplate | null
  open: boolean
  onClose: () => void
}) {
  if (!template) return null
  const sections = Array.isArray(template.sections)
    ? (template.sections as TemplateSection[])
    : []

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {template.name}
            {template.isSystem && (
              <Badge variant="outline" className="text-primary border-primary text-xs font-normal">
                Préconfiguré
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>
        {template.description && (
          <p className="text-sm text-muted-foreground">{template.description}</p>
        )}
        <div className="space-y-4 mt-2">
          {sections.map((section) => (
            <div key={section.id} className="border rounded-md p-4 space-y-2">
              <h4 className="font-medium text-sm">{section.title}</h4>
              <ul className="space-y-1">
                {section.fields.map((field) => (
                  <li key={field.id} className="text-sm flex items-center gap-2 text-muted-foreground">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                    <span>{field.label}</span>
                    {field.unit && (
                      <span className="text-xs text-muted-foreground/70">({field.unit})</span>
                    )}
                    {field.required && (
                      <span className="text-xs text-red-500 ml-auto">obligatoire</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export type TemplateLibraryProps = {
  initialMode?: 'list' | 'create'
  /** Story 9.2 — ouvre la prévisualisation de ce template après le chargement. */
  initialPreviewTemplateId?: string | null
  onInitialPreviewConsumed?: () => void
}

export function TemplateLibrary({
  initialMode = 'list',
  initialPreviewTemplateId = null,
  onInitialPreviewConsumed,
}: TemplateLibraryProps = {}) {
  const previewConsumedRef = useRef(false)
  const initialPreviewIdRef = useRef(initialPreviewTemplateId)
  const onConsumedRef = useRef(onInitialPreviewConsumed)
  onConsumedRef.current = onInitialPreviewConsumed
  const [templates, setTemplates] = useState<FormTemplate[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [previewTemplate, setPreviewTemplate] = useState<FormTemplate | null>(null)
  const [editorMode, setEditorMode] = useState<'list' | 'create' | 'edit'>(initialMode)
  const [editingTemplate, setEditingTemplate] = useState<FormTemplate | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<FormTemplate | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isDuplicating, setIsDuplicating] = useState<string | null>(null)

  useEffect(() => {
    ;(async () => {
      try {
        setIsLoading(true)
        const data = await apiListTemplates()
        setTemplates(data)
        const wantedId = initialPreviewIdRef.current
        if (wantedId && !previewConsumedRef.current) {
          previewConsumedRef.current = true
          const found = data.find((t) => t.id === wantedId)
          if (found) setPreviewTemplate(found)
          onConsumedRef.current?.()
        }
      } catch (e: unknown) {
        console.error('[TemplateLibrary] Erreur chargement templates:', e)
        if (initialPreviewIdRef.current && !previewConsumedRef.current) {
          previewConsumedRef.current = true
          onConsumedRef.current?.()
        }
        toast.error('Erreur lors du chargement des templates', { duration: Infinity })
      } finally {
        setIsLoading(false)
      }
    })()
  }, [])

  const systemTemplates = templates.filter((t) => t.isSystem)
  const orgTemplates = templates.filter((t) => !t.isSystem)

  const getSectionCount = (template: FormTemplate): number => {
    if (!Array.isArray(template.sections)) return 0
    return (template.sections as TemplateSection[]).length
  }

  const handleSaveSuccess = async () => {
    setEditorMode('list')
    setEditingTemplate(null)
    try {
      setIsLoading(true)
      const data = await apiListTemplates()
      setTemplates(data)
    } catch (e: unknown) {
      toast.error('Erreur lors du rechargement des templates', { duration: Infinity })
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return
    setIsDeleting(true)
    try {
      await apiDeleteTemplate(deleteTarget.id)
      toast.success(`Template "${deleteTarget.name}" supprimé`, { duration: 4000 })
      setDeleteTarget(null)
      const data = await apiListTemplates()
      setTemplates(data)
    } catch (e: unknown) {
      toast.error('Erreur lors de la suppression du template', { duration: Infinity })
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDuplicate = async (template: FormTemplate) => {
    setIsDuplicating(template.id)
    try {
      await apiDuplicateTemplate(template.id)
      toast.success(`"Copie de ${template.name}" créé`, { duration: 4000 })
      const data = await apiListTemplates()
      setTemplates(data)
    } catch (e: unknown) {
      const is403 = e instanceof Error && e.message.includes(' 403:')
      toast.error(
        is403
          ? 'Accès interdit : vous ne pouvez pas dupliquer ce template'
          : 'Erreur lors de la duplication du template',
        { duration: Infinity }
      )
    } finally {
      setIsDuplicating(null)
    }
  }

  // Si en mode éditeur, afficher TemplateEditor à la place de la liste
  if (editorMode !== 'list') {
    return (
      <TemplateEditor
        template={editorMode === 'edit' ? editingTemplate : null}
        onSave={handleSaveSuccess}
        onCancel={() => {
          setEditorMode('list')
          setEditingTemplate(null)
        }}
      />
    )
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Bibliothèque de Templates</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Gérez vos formulaires de collecte de données environnementales
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => setEditorMode('create')}
        >
          Nouveau template
        </Button>
      </div>

      {/* Skeleton pendant le chargement */}
      {isLoading && (
        <div className="space-y-3">
          <TemplateSkeleton />
          <TemplateSkeleton />
          <TemplateSkeleton />
        </div>
      )}

      {/* Contenu chargé */}
      {!isLoading && (
        <>
          {templates.length === 0 && (
            <EmptyState
              icon="📄"
              title="Aucun template"
              description="Créez votre premier template de formulaire pour structurer la collecte de données fournisseurs."
              primaryLabel="Générer avec l'agent"
              secondaryLabel="+ Créer manuellement"
              onSecondaryClick={() => setEditorMode('create')}
            />
          )}

          {/* Templates préconfigurés */}
          {systemTemplates.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Templates préconfigurés
              </h3>
              <div className="space-y-3">
                {systemTemplates.map((template) => (
                  <div
                    key={template.id}
                    className="rounded-lg border bg-white/80 p-5 space-y-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-1">
                        <h4 className="font-medium text-gray-900">{template.name}</h4>
                        <p className="text-xs text-muted-foreground">
                          {(() => { const n = getSectionCount(template); return `${n} section${n !== 1 ? 's' : ''}` })()}
                        </p>
                      </div>
                      <Badge variant="outline" className="text-primary border-primary shrink-0">
                        Préconfiguré
                      </Badge>
                    </div>
                    {template.description && (
                      <p className="text-sm text-muted-foreground">{template.description}</p>
                    )}
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setPreviewTemplate(template)}
                      >
                        Aperçu
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDuplicate(template)}
                        disabled={isDuplicating === template.id}
                      >
                        Dupliquer
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Templates organisation */}
          {orgTemplates.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Templates personnalisés
              </h3>
              <div className="space-y-3">
                {orgTemplates.map((template) => (
                  <div
                    key={template.id}
                    className="rounded-lg border bg-white/80 p-5 space-y-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-1">
                        <h4 className="font-medium text-gray-900">{template.name}</h4>
                        <p className="text-xs text-muted-foreground">
                          {(() => { const n = getSectionCount(template); return `${n} section${n !== 1 ? 's' : ''}` })()}
                        </p>
                      </div>
                    </div>
                    {template.description && (
                      <p className="text-sm text-muted-foreground">{template.description}</p>
                    )}
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setPreviewTemplate(template)}
                      >
                        Aperçu
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setEditingTemplate(template); setEditorMode('edit') }}
                      >
                        Modifier
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDuplicate(template)}
                        disabled={isDuplicating === template.id}
                      >
                        Dupliquer
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:text-red-700 hover:border-red-300"
                        onClick={() => setDeleteTarget(template)}
                      >
                        Supprimer
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Dialog aperçu */}
      <TemplatePreviewDialog
        template={previewTemplate}
        open={previewTemplate !== null}
        onClose={() => setPreviewTemplate(null)}
      />

      {/* AlertDialog confirmation de suppression */}
      <AlertDialog open={deleteTarget !== null} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le template ?</AlertDialogTitle>
            <AlertDialogDescription>
              Vous êtes sur le point de supprimer "{deleteTarget?.name}". Cette action est irréversible.
              Les campagnes existantes utilisant ce template ne seront pas affectées.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? 'Suppression...' : 'Supprimer définitivement'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
